import React, {useCallback, useContext, useEffect, useState} from "react";
import {
  Icon,
  Segment,
  Statistic,
  StatisticGroup,
  StatisticLabel,
  StatisticValue,
  Header, Loader, Button, Grid, Divider, Popup, Form
} from "semantic-ui-react";
import RestClientInstance from "../../../../lib/RestClient";
import {
  Bar,
  Line,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  LineChart,
  ScatterChart, ZAxis, Scatter, PieChart, Pie, Cell
} from "recharts";
import moment from "moment";
import {translateAnalyticsPage} from "../../services/Analytics";
import i18n from "../../../../locale/i18n";
import {AnalyticsTracking} from "../../components/AnalyticsTracking/AnalyticsTracking";
import {default as DatePicker} from "react-datepicker/es";
import {stringToColour} from "../../utils/colors";

const t = i18n.t.bind(i18n);

//
// Responsive Context
//
// may be useful for entire application too

const getResponsiveState = () => ({
  width: window.innerWidth,
  isMobile: window.innerWidth <= 960
});

const ResponsiveContext = React.createContext(getResponsiveState());
const ResponsiveContextProvider = (props: {children: React.ReactNode}) => {
  const [contextValue, setContextValue] = useState(getResponsiveState());

  useEffect(() => {
    const onResize = () => {
      setContextValue(getResponsiveState());
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    }
  }, []);

  return <ResponsiveContext.Provider value={contextValue}>
    {props.children}
  </ResponsiveContext.Provider>
}

const useResponsive = () => {
  return useContext(ResponsiveContext);
}

//
//

const TimeFrameDisplay = (props: {dateFrom: string; dateTo: string;}) => {
  return <>{moment(props.dateFrom).format('DD.MM.YYYY')} - {moment(props.dateTo).format('DD.MM.YYYY')}</>;
};

const CurrentlyOnline = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [stats, setStats] = useState<any>({});
  const {isMobile} = useResponsive();
  const refreshSeconds = 15;

  useEffect(() => {
    setLoading(true);
    RestClientInstance.get('infosys-reports/online-report')
      .then(response => {
        setStats(response.data);
        setLoading(false);
      }).catch(error => {
        // TODO error handling
      });
    let refreshInterval = setInterval(() => {
      RestClientInstance.get('infosys-reports/online-report')
        .then(response => { setStats(response.data); })
        .catch(error => { /* no error handling for background refresh */ });
    }, refreshSeconds * 1000);
    return () => {
      clearInterval(refreshInterval);
    }
  }, []);

  return <Segment raised >
    <Header as="h2">
      <Icon name="globe" />
      <Header.Content>Aktuell Online <Popup trigger={<Icon circular name='info' size="tiny" />} wide='very'>
        <strong>Aktive Sitzungen</strong> ist die Anzahl der aktuell im InfoSys aktiven Benutzer & Gäste. <br />
        <strong>Benutzer</strong> sind dabei die angemeldeten User, <strong>Gäste</strong> sind Besucher, die sich noch nicht angemeldet haben (und sich z.B. nur die Anfuhrdaten ansehen). <br /><br />
        Aktiv bedeutet, dass das InfoSys Fenster auf dem Gerät des Nutzers in den letzten 5 Minuten geöffnet war. Ist ein Benutzer auf zwei Geräten gleichzeitig aktiv, wird er hier auch zwei mal gezählt.
      </Popup></Header.Content>
      <Header.Subheader>Wird alle {refreshSeconds} Sekunden aktualisiert</Header.Subheader>
    </Header>
    <StatisticGroup widths="5" horizontal={isMobile}>
      <Statistic>
        <StatisticValue>{loading && <Loader active inline />}{stats && stats.total}</StatisticValue>
        <StatisticLabel>Aktive Sitzungen</StatisticLabel>
      </Statistic>
      <Statistic>
        <StatisticValue>{loading && <Loader active inline />}{stats && stats.users}</StatisticValue>
        <StatisticLabel>Davon Benutzer</StatisticLabel>
      </Statistic>
      <Statistic>
        <StatisticValue>{loading && <Loader active inline />}{stats && stats.guests}</StatisticValue>
        <StatisticLabel>Davon Gäste</StatisticLabel>
      </Statistic>
    </StatisticGroup>
  </Segment>
};

const TopPages = React.memo((props: {dateFrom: string; dateTo: string;}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [, setStats] = useState<any[]>([]);
  const [top10, setTop10] = useState<any[]>([]);
  const {isMobile} = useResponsive();

  useEffect(() => {
    setLoading(true);
    let daysToFetch = props.dateFrom + '-' + props.dateTo;
    RestClientInstance.get('infosys-reports/top-pages', {dateFrom: props.dateFrom, dateTo: props.dateTo})
      .then(response => {
        if(daysToFetch !== props.dateFrom + '-' + props.dateTo) {
          return;
        }
        setStats(response.data);
        setTop10((response.data.length > 10) ? response.data.slice(0,10) : response.data);
        setLoading(false);
      }).catch(error => {
      // TODO error handling
    });
  }, [props.dateFrom, props.dateTo]);

  return <Segment raised loading={loading}>
    <Header as="h3">
      <Header.Content>Top-Seiten <Popup trigger={<Icon circular name='info' size="small" />} wide='very'>
        <strong>Top-Seiten</strong> sind die Seiten, die am häufigsten aufgerufen wurden. Hier sortiert nach der absoluten Anzahl über den gewählten Zeitraum.
      </Popup></Header.Content>
      <Header.Subheader><TimeFrameDisplay dateFrom={props.dateFrom} dateTo={props.dateTo} /></Header.Subheader>
    </Header>
    {!loading && top10 && (
      <ResponsiveContainer width="100%" height={50 * top10.length}>
        <BarChart layout={"vertical"}
                  data={top10}>
          <XAxis type="number" />
          <YAxis dataKey="action" type="category" width={isMobile ? 100 : 200} tickFormatter={value => translateAnalyticsPage(value, t)}


          />
          <Tooltip/>
          <CartesianGrid strokeDasharray="3 3" />
          <Bar dataKey="count" fill="rgb(31,161,46)" fillOpacity={0.75} stroke="rgb(20,107,30)" strokeWidth={1} />
        </BarChart>
      </ResponsiveContainer>
    )}

  </Segment>
});


const ExitPages = (props: {dateFrom: string; dateTo: string;}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [, setStats] = useState<any[]>([]);
  const [top10, setTop10] = useState<any[]>([]);
  const {isMobile} = useResponsive();
  

  useEffect(() => {
    setLoading(true);
    let daysToFetch = props.dateFrom + '-' + props.dateTo;
    RestClientInstance.get('infosys-reports/exit-pages', {dateFrom: props.dateFrom, dateTo: props.dateTo})
      .then(response => {
        if(daysToFetch !== props.dateFrom + '-' + props.dateTo) {
          return;
        }
        setStats(response.data);
        setTop10((response.data.length > 10) ? response.data.slice(0,10) : response.data);
        setLoading(false);
      }).catch(error => {
      // TODO error handling
    });
  }, [props.dateFrom, props.dateTo]);

  return <Segment raised loading={loading}>
    <Header as="h3">
      <Header.Content>Ausstiegsseiten <Popup trigger={<Icon circular name='info' size="small" />} wide='very'>
        <strong>Ausstiegsseiten</strong> sind die Seiten, auf denen das InfoSys verlassen wurde - also die <strong>letzte Aktion</strong> in einer <strong>Sitzung</strong>. Hier sortiert nach der absoluten Anzahl über den gewählten Zeitraum.
      </Popup></Header.Content>
      <Header.Subheader><TimeFrameDisplay dateFrom={props.dateFrom} dateTo={props.dateTo} /></Header.Subheader>
    </Header>

    {!loading && top10 && (
      <ResponsiveContainer width="100%" height={50 * top10.length}>
        <BarChart layout={"vertical"}
                  data={top10}>
          <XAxis type="number" />
          <YAxis dataKey="lastAction" type="category" width={isMobile ? 100 : 200} tickFormatter={value => translateAnalyticsPage(value, t)} />
          <Tooltip/>
          <CartesianGrid strokeDasharray="3 3" />
          <Bar dataKey="count" fill="rgb(31,161,46)" fillOpacity={0.75} stroke="rgb(20,107,30)" strokeWidth={1}/>
        </BarChart>
      </ResponsiveContainer>
    )}

  </Segment>
};

const DeviceTypes = (props: {dateFrom: string; dateTo: string;}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [stats, setStats] = useState<any[]>([]);

  const {isMobile} = useResponsive();

  useEffect(() => {
    setLoading(true);
    let daysToFetch = props.dateFrom + '-' + props.dateTo;
    RestClientInstance.get('infosys-reports/device-types', {dateFrom: props.dateFrom, dateTo: props.dateTo})
      .then(response => {
        if(daysToFetch !== props.dateFrom + '-' + props.dateTo) {
          return;
        }
        const filtered = (response.data || []).filter((d:any) => d.deviceType !== null);
        let sum = 0;
        for(let stat of filtered) {
          sum += stat.count;
        }
        for(let stat of filtered) {
          stat.percent = Math.round(10000 * (stat.count / sum))/100;
        }
        setStats(filtered);
        setLoading(false);
      }).catch(error => {
      // TODO error handling
    });
  }, [props.dateFrom, props.dateTo]);

  return <Segment raised loading={loading}>
    <Header as="h3">
      <Header.Content>Gerätetypen nach Anzahl Sitzungen <Popup trigger={<Icon circular name='info' size="small" />} wide='very'>
        Zeigt an, mit welchen Geräten am häufigsten auf das InfoSys zugegriffen wird. Datenbasis hierfür sind die einzelnen <strong>Sitzungen</strong>.
      </Popup></Header.Content>
      <Header.Subheader><TimeFrameDisplay dateFrom={props.dateFrom} dateTo={props.dateTo} /></Header.Subheader>
    </Header>
    {!loading && stats && (
      <ResponsiveContainer width="100%" height={350}>
        <PieChart>
          <Tooltip formatter={(v) => v + ' %'} />
          <Pie outerRadius={isMobile ? 80 : 110} isAnimationActive={false} data={stats} dataKey="percent" labelLine={true} nameKey="deviceType" label={(entry) => entry.name}>
            {stats.map((stat, idx) => {
              return <Cell key={idx} fill={stringToColour(stat.deviceType)}  />
            })}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    )}

  </Segment>
};

const SessionsPerDay = (props: {dateFrom: string; dateTo: string;}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [stats, setStats] = useState<any[]>([]);

  const {isMobile} = useResponsive();

  useEffect(() => {
    setLoading(true);
    let daysToFetch = props.dateFrom + '-' + props.dateTo;
    RestClientInstance.get('infosys-reports/sessions-per-day', {dateFrom: props.dateFrom, dateTo: props.dateTo})
      .then(response => {
        if(daysToFetch !== props.dateFrom + '-' + props.dateTo) {
          return;
        }
        setStats(response.data.map((row: any) => ({
          ...row,
          dateNumber: (new Date(row.date)).getTime()
        })));
        setLoading(false);
      }).catch(error => {
      // TODO error handling
    });
  }, [props.dateFrom, props.dateTo]);

  return <Segment raised loading={loading}>
    <Header as="h3">
      <Header.Content>Aktivität <Popup trigger={<Icon circular name='info' size="small" />} wide='very'>
        <strong>Sitzungen</strong> fassen den Besuch eines Nutzers über mehrere Aktionen hinweg zusammen. Loggt ein Benutzer sich ein und besucht 10 Seiten, wird dies als eine einzelne Sitzung gewertet. Ist der Benutzer gleichzeitig auf mehreren Geräten angemeldet, zählt jedes dieser Geräte als einzelne Sitzung. Diese Kennzahl zeigt also, wie oft das System als Ganzes verwendet wird. <br />
        <strong>Seitenaufrufe</strong> sind die einzelnen Aktionen, wie bspw Abruf der Logistikdaten, Aufruf der Startseite, etc. Diese Kennzahl zeigt, wie intensiv das System verwendet wird.
      </Popup></Header.Content>
      <Header.Subheader><TimeFrameDisplay dateFrom={props.dateFrom} dateTo={props.dateTo} /></Header.Subheader>
    </Header>
    {!loading && stats && (
      <ResponsiveContainer width="100%" height={!isMobile ? 500 : 300}>
        <LineChart data={stats}>
          <XAxis dataKey="dateNumber" type="number" scale="time"  domain = {['auto', 'auto']}
                 tickFormatter = {(unixTime) => moment(unixTime).format('DD.MM')}/>
          <YAxis yAxisId="A" label={{ value: 'Sitzungen', angle: -90, position: 'insideLeft' }} />
          {!isMobile && <YAxis yAxisId="B" label={{ value: 'Seitenaufrufe', angle: 90, position: 'insideRight' }} orientation="right" />}
          <Tooltip labelFormatter={(unixTime) => moment(unixTime).format('dddd, DD.MM.YYYY')} />
          <CartesianGrid strokeDasharray="3 3" />
          <Line yAxisId="A" dataKey="sessionCount" name="Sitzungen" fill="rgb(31,161,46)" fillOpacity={0.75} stroke="rgb(20,107,30)" strokeWidth={1} />
          {!isMobile && <Line yAxisId="B" dataKey="requestCount" name="Seitenaufrufe" fill="rgb(31,161,46)" fillOpacity={0.75} stroke="rgb(24,66,136)" strokeWidth={1}/>}
        </LineChart>
      </ResponsiveContainer>
    )}

  </Segment>
};

const UsersPerDay = (props: {dateFrom: string; dateTo: string;}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [stats, setStats] = useState<any[]>([]);
  

  useEffect(() => {
    setLoading(true);
    let daysToFetch = props.dateFrom + '-' + props.dateTo;
    RestClientInstance.get('infosys-reports/users-per-day', {dateFrom: props.dateFrom, dateTo: props.dateTo})
      .then(response => {
        if(daysToFetch !== props.dateFrom + '-' + props.dateTo) {
          return;
        }
        setStats(response.data.map((row: any) => ({
          ...row,
          dateNumber: (new Date(row.date)).getTime()
        })));
        setLoading(false);
      }).catch(error => {
      // TODO error handling
    });
  }, [props.dateFrom, props.dateTo]);

  return <Segment raised loading={loading}>
    <Header as="h3">
      <Header.Content>Eindeutige Benutzer <Popup trigger={<Icon circular name='info' size="small" />} wide='very'>
        <strong>Eindeutige Benutzer</strong> ist die Anzahl der unterschiedlichen angemeldeten Benutzer in einem Zeitraum. Meldet sich ein Benutzer sowohl am Vormittag als auch am Abend an, wird er in diesem Zeitraum nur einmal gezählt.
      </Popup></Header.Content>
      <Header.Subheader><TimeFrameDisplay dateFrom={props.dateFrom} dateTo={props.dateTo} /></Header.Subheader>
    </Header>
    {!loading && stats && (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={stats}>
          <XAxis dataKey="dateNumber" type="number" scale="time"  domain = {['auto', 'auto']}
                 tickFormatter = {(unixTime) => moment(unixTime).format('DD.MM')}/>
          <YAxis yAxisId="A" label={{ value: 'Benutzer', angle: -90, position: 'insideLeft' }} />
          <Tooltip labelFormatter={(unixTime) => moment(unixTime).format('dddd, DD.MM.YYYY')} />
          <CartesianGrid strokeDasharray="3 3" />
          <Line yAxisId="A" dataKey="userCount" fill="rgb(31,161,46)" fillOpacity={0.75} stroke="rgb(20,107,30)" strokeWidth={1}/>
        </LineChart>
      </ResponsiveContainer>
    )}

  </Segment>
};

const Summary = (props: {dateFrom: string; dateTo: string;}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [stats, setStats] = useState<any>({});

  const {isMobile} = useResponsive();

  useEffect(() => {
    setLoading(true);
    let daysToFetch = props.dateFrom + '-' + props.dateTo;
    RestClientInstance.get('infosys-reports/summary', {dateFrom: props.dateFrom, dateTo: props.dateTo})
      .then(response => {
        if(daysToFetch !== props.dateFrom + '-' + props.dateTo) {
          return;
        }
        setStats(response.data || {});
        setLoading(false);
      }).catch(error => {
      // TODO error handling
    });
  }, [props.dateFrom, props.dateTo]);

  return <Segment raised loading={loading} >
    <Header as="h3">
      <Header.Content>Zusammenfassung <Popup trigger={<Icon circular name='info' size="small" />} wide='very'>
        Die selben Kennziffern wie in den beiden vorherigen Diagrammen - allerdings dieses mal auf den gesamten Zeitraum und nicht auf einzelne Tage bezogen. Ein Benutzer wird also hier auch dann nur einmal gezählt, wenn er an mehreren Tagen aktiv war.
      </Popup></Header.Content>
      <Header.Subheader><TimeFrameDisplay dateFrom={props.dateFrom} dateTo={props.dateTo} /></Header.Subheader>
    </Header>
    {!loading && <StatisticGroup widths="5" horizontal={isMobile}>
      <Statistic>
        <StatisticValue>{loading && <Loader active inline />}{!loading && stats && (stats.userCount || 0).toLocaleString()}</StatisticValue>
        <StatisticLabel>Eindeutige Benutzer</StatisticLabel>
      </Statistic>
      <Statistic>
        <StatisticValue>{loading && <Loader active inline />}{!loading && stats && (stats.sessionCount || 0).toLocaleString()}</StatisticValue>
        <StatisticLabel>Sitzungen</StatisticLabel>
      </Statistic>
      <Statistic>
        <StatisticValue>{loading && <Loader active inline />}{!loading && stats && (stats.requestCount || 0).toLocaleString()}</StatisticValue>
        <StatisticLabel>Seitenaufrufe</StatisticLabel>
      </Statistic>
    </StatisticGroup>}
  </Segment>
};

const SessionsDoWHour = React.memo((props: {dateFrom: string; dateTo: string;}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [stats, setStats] = useState<any[]>([]);
  const {isMobile} = useResponsive();

  useEffect(() => {
    setLoading(true);
    let daysToFetch = props.dateFrom + '-' + props.dateTo;
    RestClientInstance.get('infosys-reports/sessions-dow-hour', {dateFrom: props.dateFrom, dateTo: props.dateTo})
      .then(response => {
        if(daysToFetch !== props.dateFrom + '-' + props.dateTo) {
          return;
        }
        const dataSets: { count: number; hour: number; dayOfWeek: number; percentage: number; }[][] = [];
        let max = 0;
        for(let i = 0; i < 7; i++) {
          dataSets[i] = [];
          for(let j = 0; j < 24; j++) {
            dataSets[i][j] = {count: 0, hour: j, dayOfWeek: i+1, percentage: 0};
          }
        }
        for(let row of response.data) {
          max = Math.max(max, row.sessionCount);
        }
        for(let row of response.data) {
          const hour = row.hour;
          const dayOfWeek = row.dayOfWeek;
          if(dayOfWeek >= 1 && dayOfWeek <= 7 && hour >= 0 && hour <= 23) {
            dataSets[dayOfWeek - 1][hour].count = row.sessionCount;
            dataSets[dayOfWeek - 1][hour].percentage = Math.round(700 * row.sessionCount / max);
          }
        }
        setStats(dataSets);
        setLoading(false);
      }).catch(error => {
      // TODO error handling
    });
  }, [props.dateFrom, props.dateTo]);

  const dowMap = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
  const getDowName = (dow: number) => {
    if(dow >= 1 && dow <= dowMap.length) {
      return dowMap[dow-1];
    }
    return '';
  }
  return <Segment raised loading={loading}>
    <Header as="h3">
      <Header.Content>Sitzungsanzahl nach Tag & Stunde <Popup trigger={<Icon circular name='info' size="small" />} wide='very'>
        Die Anzahl der <strong>Sitzungen</strong> nach <strong>Wochentag</strong> und <strong>Stunde</strong> zeigt an, zu welchen Uhrzeiten an welchen Wochentagen das System am häufigsten genutzt wird.
      </Popup></Header.Content>
      <Header.Subheader><TimeFrameDisplay dateFrom={props.dateFrom} dateTo={props.dateTo} /></Header.Subheader>
    </Header>
    {!loading && stats && stats.length >= 7 && (
      <ResponsiveContainer width="100%" height={250}>
        <ScatterChart margin={{top: 10}} >
          <XAxis type="number" dataKey="hour" domain={[0,23]} interval={isMobile ? 1 : 0} tickCount={24} minTickGap={0} name="Stunde" />
          <YAxis type="number" width={100} dataKey="dayOfWeek" tickFormatter = {(dow) => getDowName(dow)} tickCount={8} interval={0} name="Wochentag" />
          <ZAxis type="number" dataKey="percentage" range={[0,700]} scale="auto" name="Anzahl" />
          <Tooltip formatter={(value, name, entry, index) => {
            if(name === 'Stunde') {
              return value;
            } else if(name === 'Wochentag') {
              return getDowName(entry.payload.dayOfWeek);
            } else if(name === 'Anzahl') {
              return entry.payload.count;
            } else {
              return value;
            }
          }} />
          <CartesianGrid strokeDasharray="1 1" />
          {stats.map((stat, idx) => {
            return <Scatter key={idx} data={stat} fill="rgb(31,161,46)"  />
          })}
        </ScatterChart>
      </ResponsiveContainer>
    )}

  </Segment>
});

const SessionsPerCategory = (props: {dateFrom: string; dateTo: string;}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [stats, setStats] = useState<any[]>([]);
  const [helpData, setHelpData] = useState<any[]>([]);

  const {isMobile} = useResponsive();

  useEffect(() => {
    setLoading(true);
    let daysToFetch = props.dateFrom + '-' + props.dateTo;
    RestClientInstance.get('infosys-reports/sessions-per-category', {dateFrom: props.dateFrom, dateTo: props.dateTo})
      .then(response => {
        if(daysToFetch !== props.dateFrom + '-' + props.dateTo) {
          return;
        }
        const filtered = (response.data.result || []).filter((d:any) => d.userId !== null);
        let sum = 0;
        for(let stat of filtered) {
          sum += stat.count;
        }
        for(let stat of filtered) {
          stat.percent = Math.round(10000 * (stat.count / sum))/100;
        }
        filtered.sort((a: any,b :any) => {
          return -1 * (a.percent < b.percent ? -1 : (a.percent > b.percent ? 1 : 0));
        });
        setStats(filtered);
        setHelpData(response.data.categories || []);
        setLoading(false);
      }).catch(error => {
      // TODO error handling
    });
  }, [props.dateFrom, props.dateTo]);

  return <Segment raised loading={loading}>
    <Header as="h3">
      <Header.Content>Bereiche nach Anzahl Sitzungen <Popup trigger={<Icon circular name='info' size="small" />} wide='very'>
        Zeigt an, welche <strong>Bereiche</strong> in wie vielen <strong>Sitzungen</strong> im angegebenen Zeitraum verwendet wurden. <br />
        Die Kategorien setzen sich aus folgenden Seiten zusammen (jeweils mit allen Unterseiten): <br />
        <ul>
          {helpData.map((category: {name: string, pages: string[]}) => (
            <li key={category.name}>
              <strong>{category.name}</strong>
              <ul>
                {category.pages.map(page => (<li key={page}>
                  {translateAnalyticsPage(page, t)}
                </li>))}
              </ul>
            </li>
          ))}
        </ul>
      </Popup></Header.Content>
      <Header.Subheader><TimeFrameDisplay dateFrom={props.dateFrom} dateTo={props.dateTo} /></Header.Subheader>
    </Header>
    {!loading && stats && (
      <ResponsiveContainer width="100%" height={350}>
        <PieChart>
          <Tooltip formatter={(v) => v + ' %'} />
          <Pie outerRadius={isMobile ? 80 : 110}  isAnimationActive={false} data={stats} dataKey="percent" labelLine={true} nameKey="category" label={(entry) => entry.name}>
            {stats.map((stat, idx) => {
              let color = stringToColour(stat.category);

              return <Cell key={idx} fill={color}  />
            })}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    )}

  </Segment>
};

const UsersPerCategory = (props: {dateFrom: string; dateTo: string;}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [stats, setStats] = useState<any[]>([]);
  const [helpData, setHelpData] = useState<any[]>([]);

  const {isMobile} = useResponsive();

  useEffect(() => {
    setLoading(true);
    let daysToFetch = props.dateFrom + '-' + props.dateTo;
    RestClientInstance.get('infosys-reports/users-per-category', {dateFrom: props.dateFrom, dateTo: props.dateTo})
      .then(response => {
        if(daysToFetch !== props.dateFrom + '-' + props.dateTo) {
          return;
        }
        const filtered = (response.data.result || []).filter((d:any) => d.userId !== null);
        let sum = 0;
        for(let stat of filtered) {
          sum += stat.count;
        }
        for(let stat of filtered) {
          stat.percent = Math.round(10000 * (stat.count / sum))/100;
        }
        filtered.sort((a: any,b :any) => {
          return -1 * (a.percent < b.percent ? -1 : (a.percent > b.percent ? 1 : 0));
        });
        setStats(filtered);
        setHelpData(response.data.categories || []);
        setLoading(false);
      }).catch(error => {
         // TODO error handling
      });
  }, [props.dateFrom, props.dateTo]);

  return <Segment raised loading={loading}>
    <Header as="h3">
      <Header.Content>Bereiche nach Anzahl eindeutiger Benutzer <Popup trigger={<Icon circular name='info' size="small" />} wide='very'>
        Zeigt an, welche <strong>Bereiche</strong> von wie vielen eindeutigen Benutzern im angegebenen Zeitraum verwendet wurden. <br />
        Die Kategorien setzen sich aus folgenden Seiten zusammen (jeweils mit allen Unterseiten): <br />
        <ul>
          {helpData.map((category: {name: string, pages: string[]}) => (
            <li key={category.name}>
              <strong>{category.name}</strong>
              <ul>
                {category.pages.map(page => (<li key={page}>
                  {translateAnalyticsPage(page, t)}
                </li>))}
              </ul>
            </li>
          ))}
        </ul>
      </Popup></Header.Content>
      <Header.Subheader><TimeFrameDisplay dateFrom={props.dateFrom} dateTo={props.dateTo} /></Header.Subheader>
    </Header>
    {!loading && stats && (
      <ResponsiveContainer width="100%" height={350}>
        <PieChart>
          <Tooltip formatter={(v) => v + ' %'} />
          <Pie outerRadius={isMobile ? 80 : 110}  isAnimationActive={false} data={stats} dataKey="percent" labelLine={true} nameKey="category" label={(entry) => entry.name}>
            {stats.map((stat, idx) => {
              let color = stringToColour(stat.category);

              return <Cell key={idx} fill={color}  />
            })}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    )}

  </Segment>
};


const getDaysAgoAsIsoDateString = (daysAgo: number) => {
  const now = new Date();
  const resultDate = new Date(+now - daysAgo * 60 * 60 * 24 * 1000);
  return resultDate.toISOString().split('T')[0];
}

const DEFAULT_TO = getDaysAgoAsIsoDateString(1);
const DEFAULT_FROM = getDaysAgoAsIsoDateString(14);

const AnalyticsReportComponent = React.memo(() => {
  const [dateFrom, setDateFrom] = useState<string>(DEFAULT_FROM);
  const [dateTo, setDateTo] = useState<string>(DEFAULT_TO);

  const {isMobile} = useResponsive();

  const setDays = useCallback((days: number) => {
    const to = getDaysAgoAsIsoDateString(1);
    const from = getDaysAgoAsIsoDateString(days);
    setDateTo(to);
    setDateFrom(from);
  }, []);
  
  return (<>
    <AnalyticsTracking page="infosys_statistics" />
    {!isMobile && <Header as="h1">Statistik</Header>}
    {isMobile && <Segment><Header as="h1">Statistik</Header></Segment>}
    <CurrentlyOnline />
    <Divider />
    <Segment raised>
      <Header as="h2">
        <Icon name="calendar alternate outline" />
        <Header.Content>Zeitraum</Header.Content>
      </Header>
      <Form>
        <Form.Group>
          <Form.Field width={isMobile ? '16' : undefined}>
            <label>Datum von</label>
            <DatePicker
              selected={new Date(dateFrom)}
              dateFormat={t("Common.DateFormatPicker")}
              maxDate={new Date(dateTo)}
              onChange={(date: Date) => {
                if(date) {setDateFrom(date.toISOString().split('T')[0]);}
              }}
            />
          </Form.Field>

          <Form.Field width={isMobile ? '16' : undefined}>
            <label>Datum bis</label>
            <DatePicker
              selected={new Date(dateTo)}
              dateFormat={t("Common.DateFormatPicker")}
              minDate={new Date(dateFrom)}
              onChange={(date: Date) => {
                if(date) {setDateTo(date.toISOString().split('T')[0]);}
              }}
            />
          </Form.Field>

          <Form.Field style={!isMobile ? {marginLeft: 'auto'} : {}} width={isMobile ? '16' : undefined}>
            <label style={!isMobile ? {textAlign: 'right'} : {}}>Schnellauswahl</label>
            <div>
              <Button.Group vertical={isMobile} fluid={isMobile}>
                <Button onClick={() => setDays(365)}>365 Tage</Button>
                <Button onClick={() => setDays(90)}>90 Tage</Button>
                <Button onClick={() => setDays(30)}>30 Tage</Button>
                <Button onClick={() => setDays(14)}>14 Tage</Button>
                <Button onClick={() => setDays(7)}>7 Tage</Button>
              </Button.Group>
            </div>
          </Form.Field>
        </Form.Group>
      </Form>
    </Segment>
    <Grid stackable>
      <Grid.Column width="16">
        <SessionsPerDay dateFrom={dateFrom} dateTo={dateTo} />
      </Grid.Column>
      <Grid.Column width="16">
        <UsersPerDay dateFrom={dateFrom} dateTo={dateTo} />
      </Grid.Column>
      <Grid.Column width="16">
        <Summary dateFrom={dateFrom} dateTo={dateTo} />
      </Grid.Column>
      <Grid.Column width="8">
        <TopPages dateFrom={dateFrom} dateTo={dateTo} />
      </Grid.Column>
      <Grid.Column width="8">
        <ExitPages dateFrom={dateFrom} dateTo={dateTo} />
      </Grid.Column>
      <Grid.Column width="16">
        <SessionsDoWHour dateFrom={dateFrom} dateTo={dateTo} />
      </Grid.Column>
      <Grid.Row columns={3}>
      <Grid.Column >
        <DeviceTypes dateFrom={dateFrom} dateTo={dateTo} />
      </Grid.Column>
      <Grid.Column >
        <UsersPerCategory dateFrom={dateFrom} dateTo={dateTo} />
      </Grid.Column>
      <Grid.Column >
        <SessionsPerCategory dateFrom={dateFrom} dateTo={dateTo} />
      </Grid.Column>
      </Grid.Row>
    </Grid>
    </>);
});

export const AnalyticsReport = () => {
  return <ResponsiveContextProvider>
    <AnalyticsReportComponent />
  </ResponsiveContextProvider>
}